import { FilingStatus } from '#dictionary/filingStatuses'

export const CTC_AMOUNT_PER_CHILD = 2000
export const CTC_PHASEOUT_REDUCTION_PER_1K = 50

// Social Security taxation threshold objects
const SS_THRESHOLD_SINGLE = { 50: 25000, 85: 34000 } as const
const SS_THRESHOLD_MFJ = { 50: 32000, 85: 44000 } as const

/**
 * Social Security taxation thresholds by filing status.
 * Benefits are taxed at 0%, 50%, or 85% depending on provisional income.
 *
 * Structure: SOCIAL_SECURITY_THRESHOLD[filingStatus][50 | 85]
 * - 50: Income threshold where 50% taxation begins
 * - 85: Income threshold where 85% taxation begins
 */
export const SOCIAL_SECURITY_THRESHOLD = {
  [FilingStatus.SINGLE]: SS_THRESHOLD_SINGLE,
  [FilingStatus.MARRIED_FILING_JOINTLY]: SS_THRESHOLD_MFJ,
  [FilingStatus.MARRIED_FILING_SEPARATELY]: SS_THRESHOLD_SINGLE,
  [FilingStatus.HEAD_OF_HOUSEHOLD]: SS_THRESHOLD_SINGLE,
} as const

/**
 * Calculates the Child Tax Credit (CTC) amount based on the number of children,
 * modified adjusted gross income (MAGI), and phaseout threshold.
 *
 * The credit phases out by $50 for every $1,000 of income above the threshold.
 *
 * @param numberOfChildrenUnder17 - Number of qualifying children under 17 years old
 * @param modifiedAdjustedIncome - Modified Adjusted Gross Income (MAGI)
 * @param ctcPhaseoutThreshold - Income threshold where phaseout begins
 * @returns The calculated child tax credit amount (0 or positive)
 */
export function calculateChildTaxCredit(
  numberOfChildrenUnder17: number,
  modifiedAdjustedIncome: number,
  ctcPhaseoutThreshold: number
): number {
  const baseCredit = numberOfChildrenUnder17 * CTC_AMOUNT_PER_CHILD
  const incomeAboveThreshold = Math.max(
    0,
    modifiedAdjustedIncome - ctcPhaseoutThreshold
  )
  const phaseoutReduction =
    Math.ceil(incomeAboveThreshold / 1000) * CTC_PHASEOUT_REDUCTION_PER_1K

  return Math.max(0, baseCredit - phaseoutReduction)
}

/**
 * Calculates the taxable portion of Social Security benefits based on provisional income
 * and filing status. Social Security benefits can be taxed at 0%, 50%, or 85% depending
 * on income thresholds.
 *
 * @param provisionalIncome - Provisional income (AGI + 50% of SS benefits + tax-exempt interest)
 * @param socialSecurityBenefits - Total Social Security benefits received
 * @param filingStatus - Tax filing status
 * @returns The taxable portion of Social Security benefits
 */
export function calculateTaxableSocialSecurity(
  provisionalIncome: number,
  socialSecurityBenefits: number,
  filingStatus: FilingStatus
): number {
  const threshold50 = SOCIAL_SECURITY_THRESHOLD[filingStatus][50]
  const threshold85 = SOCIAL_SECURITY_THRESHOLD[filingStatus][85]

  // Calculate the amount taxable at 50% rate
  const taxableAt50 = Math.min(
    Math.max(0, Math.min(provisionalIncome, threshold85) - threshold50) * 0.5,
    socialSecurityBenefits * 0.5
  )

  // Calculate the amount taxable at 85% rate
  const taxableAt85 = Math.max(0, provisionalIncome - threshold85) * 0.85

  // Total taxable amount cannot exceed 85% of total benefits
  return Math.min(taxableAt50 + taxableAt85, socialSecurityBenefits * 0.85)
}
